import { useEffect, useState } from "react";
import { Button, Icon, LevelMeter, ProgressBar, Sheet, Tag } from "@xinghai/ui";
import type { Tone } from "@xinghai/ui";
import { useRecorder } from "../../lib/audio/useRecorder";
import { NOISE_PROBE_MS, gradeNoise, saveNoiseCheck } from "../../lib/audio/noiseCheck";
import type { NoiseCheck } from "../../lib/audio/noiseCheck";
import { Illustration } from "../shared/Illustration";

const TONE_BY_GRADE: Record<string, Tone> = {
  quiet: "success",
  ok: "warning",
  loud: "danger",
};

export interface NoiseCheckSheetProps {
  open: boolean;
  /** Called with the saved probe once the student taps 「继续」. */
  onDone: (check: NoiseCheck) => void;
  /** Escape hatch — the student can leave the page instead. */
  onSkip?: () => void;
}

/**
 * docs/ROADMAP.md A7 — 2 s room-noise probe before the first recording of a
 * session. Nothing is uploaded; only the RMS is kept.
 */
export function NoiseCheckSheet({ open, onDone, onSkip }: NoiseCheckSheetProps) {
  const recorder = useRecorder({ maxMs: NOISE_PROBE_MS });
  const { status, result, start, reset } = recorder;

  const [ran, setRan] = useState(false);

  useEffect(() => {
    if (!open) {
      setRan(false);
      reset();
    }
  }, [open, reset]);

  const measuring = status === "recording" || status === "requesting";
  const grade = result ? gradeNoise(result.rms) : null;

  return (
    <Sheet
      open={open}
      onClose={() => onSkip?.()}
      dismissible={false}
      title="先测一下你周围有多吵"
      size="md"
      footer={
        result ? (
          <>
            <Button
              variant="secondary"
              onClick={() => {
                reset();
                setRan(false);
              }}
            >
              重新检测
            </Button>
            <Button variant="primary" onClick={() => onDone(saveNoiseCheck(result.rms))}>
              继续
            </Button>
          </>
        ) : (
          <Button
            variant="primary"
            size="lg"
            block
            loading={measuring}
            disabled={measuring}
            onClick={() => {
              setRan(true);
              void start();
            }}
          >
            {ran ? "正在检测…" : "开始检测（2 秒）"}
          </Button>
        )
      }
    >
      <p className="gz-type-reading">
        接下来的 2 秒请保持安静、不要说话，我们只听一下房间里的背景声。
        这段声音不会上传，只用来判断这里适不适合录音。
      </p>

      {measuring && (
        <div className="xz-noise__live">
          <ProgressBar value={Math.min(1, recorder.elapsedMs / NOISE_PROBE_MS)} label="检测进度" />
          <LevelMeter rms={recorder.level.rms} peak={recorder.level.peak} label="环境噪声" />
        </div>
      )}

      {recorder.errorMessage && (
        <p className="gz-notice gz-notice--error" role="alert">
          <Icon id="ui-error" size={20} />
          {recorder.errorMessage}
        </p>
      )}

      {grade && result && (
        <div className="xz-noise__result">
          {grade.grade === "loud" && <Illustration name="guide-quiet" size="sm" />}
          <Tag tone={TONE_BY_GRADE[grade.grade] ?? "neutral"}>
            <Icon
              id={
                grade.grade === "quiet"
                  ? "ui-check-circle"
                  : grade.grade === "ok"
                    ? "ui-info"
                    : "ui-warning"
              }
              size={16}
            />
            {grade.label}
          </Tag>
          <p className="gz-type-reading">{grade.advice}</p>
        </div>
      )}
    </Sheet>
  );
}
